import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Clock } from "lucide-react";
import { Button, Input, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function BusinessAvailability({ onClose }: { onClose: () => void }) {
  const [days, setDays] = useState<any[] | null>(null);
  const [rules, setRules] = useState<any>(null);
  const [special, setSpecial] = useState<any[]>([]);

  const load = () => {
    api.availBusinessHours().then((r) => setDays(r.days)).catch((e) => toast.error(formatApiError(e)));
    api.availRules().then(setRules).catch(() => {});
    api.availSpecialDates().then((r) => setSpecial(r.special_dates)).catch(() => {});
  };
  useEffect(load, []);

  const setDay = (dow: number, patch: any) => setDays((ds) => ds!.map((d) => d.day_of_week === dow ? { ...d, ...patch } : d));
  const setWindow = (dow: number, i: number, key: string, val: string) =>
    setDays((ds) => ds!.map((d) => d.day_of_week === dow ? { ...d, windows: d.windows.map((w: any, wi: number) => wi === i ? { ...w, [key]: val } : w) } : d));
  const addWindow = (dow: number) => setDays((ds) => ds!.map((d) => d.day_of_week === dow ? { ...d, closed: false, windows: [...d.windows, { start_time: "08:00", end_time: "17:00" }] } : d));
  const rmWindow = (dow: number, i: number) => setDays((ds) => ds!.map((d) => d.day_of_week === dow ? { ...d, windows: d.windows.filter((_: any, wi: number) => wi !== i) } : d));

  const saveHours = async () => { try { const r = await api.availSetBusinessHours(days!); setDays(r.days); toast.success("Business hours saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveRules = async (patch: any) => { try { setRules(await api.availSetRules(patch)); toast.success("Rules saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const addSpecial = async () => {
    const date = (document.getElementById("sd-date") as HTMLInputElement)?.value;
    const label = (document.getElementById("sd-label") as HTMLInputElement)?.value;
    if (!date) return toast.error("Pick a date");
    try { await api.availAddSpecialDate({ date, label, closed: true, type: "holiday" }); load(); toast.success("Exception added"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="business-availability">
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Business Availability</h2>
            <p className="text-sm text-slate-500">When your business generally accepts appointments. This feeds the availability engine.</p>
          </div>
          <button onClick={onClose} data-testid="business-availability-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        {days === null ? <LoadingBlock rows={6} /> : (
          <>
            <section className="space-y-2" data-testid="ba-weekly">
              <h3 className="text-sm font-semibold text-slate-800">Weekly hours</h3>
              {days.map((d) => (
                <div key={d.day_of_week} className="rounded-lg border border-slate-200 p-3" data-testid={`ba-day-${d.day_of_week}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-24 text-sm font-medium text-slate-700">{DAYS[d.day_of_week]}</span>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500">
                      <input type="checkbox" checked={d.closed} onChange={(e) => setDay(d.day_of_week, { closed: e.target.checked })} data-testid={`ba-closed-${d.day_of_week}`} /> Closed
                    </label>
                    {!d.closed && <button onClick={() => addWindow(d.day_of_week)} className="ml-auto text-xs text-blue-600" data-testid={`ba-addwin-${d.day_of_week}`}><Plus className="inline h-3 w-3" /> Add window</button>}
                  </div>
                  {!d.closed && d.windows.map((w: any, i: number) => (
                    <div key={i} className="mt-2 flex items-center gap-2">
                      <input type="time" value={w.start_time} onChange={(e) => setWindow(d.day_of_week, i, "start_time", e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm" />
                      <span className="text-slate-400">–</span>
                      <input type="time" value={w.end_time} onChange={(e) => setWindow(d.day_of_week, i, "end_time", e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm" />
                      <button onClick={() => rmWindow(d.day_of_week, i)}><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" /></button>
                    </div>
                  ))}
                  {!d.closed && d.windows.length === 0 && <p className="mt-1 text-xs text-slate-400">No windows — add one or mark closed.</p>}
                </div>
              ))}
              <Button onClick={saveHours} data-testid="ba-save-hours"><Clock className="h-4 w-4" /> Save weekly hours</Button>
            </section>

            {rules && (
              <section className="mt-6 space-y-2" data-testid="ba-rules">
                <h3 className="text-sm font-semibold text-slate-800">Scheduling rules</h3>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm text-slate-600">Default duration (min)
                    <Input type="number" defaultValue={rules.default_duration_minutes} onBlur={(e) => saveRules({ default_duration_minutes: Number(e.target.value) })} data-testid="ba-default-duration" /></label>
                  <label className="text-sm text-slate-600">Min notice (min)
                    <Input type="number" defaultValue={rules.min_notice_minutes} onBlur={(e) => saveRules({ min_notice_minutes: Number(e.target.value) })} data-testid="ba-min-notice" /></label>
                  <label className="text-sm text-slate-600">Booking horizon (days)
                    <Input type="number" defaultValue={rules.booking_horizon_days} onBlur={(e) => saveRules({ booking_horizon_days: Number(e.target.value) })} data-testid="ba-horizon" /></label>
                  <label className="text-sm text-slate-600">Buffer after (min)
                    <Input type="number" defaultValue={rules.buffer_after_minutes} onBlur={(e) => saveRules({ buffer_after_minutes: Number(e.target.value) })} data-testid="ba-buffer" /></label>
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" defaultChecked={rules.same_day_allowed} onChange={(e) => saveRules({ same_day_allowed: e.target.checked })} data-testid="ba-sameday" /> Same-day booking</label>
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" defaultChecked={rules.weekend_allowed} onChange={(e) => saveRules({ weekend_allowed: e.target.checked })} data-testid="ba-weekend" /> Weekend booking</label>
                </div>
                <p className="text-xs text-slate-400">Timezone: {rules.timezone}</p>
              </section>
            )}

            <section className="mt-6 space-y-2" data-testid="ba-special">
              <h3 className="text-sm font-semibold text-slate-800">Special dates &amp; closures</h3>
              <p className="text-xs text-slate-500">Holidays and one-off changes override the weekly hours above.</p>
              <div className="flex flex-wrap items-end gap-2">
                <input id="sd-date" type="date" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="ba-sd-date" />
                <Input id="sd-label" placeholder="Label (e.g. Christmas)" className="w-48" data-testid="ba-sd-label" />
                <Button size="sm" onClick={addSpecial} data-testid="ba-sd-add"><Plus className="h-4 w-4" /> Add closure</Button>
              </div>
              {special.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`ba-sd-${s.id}`}>
                  <span>{s.date} · {s.label || s.type} · {s.closed ? "Closed" : "Custom hours"}</span>
                  <button onClick={async () => { await api.availDeleteSpecialDate(s.id); load(); }}><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" /></button>
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
